import { Helmet } from 'react-helmet-async';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e", card: "#13131a" };

const S = {
  h2: { color: COLORS.text, fontSize: '1.4rem', fontWeight: 800, margin: '40px 0 12px', letterSpacing: '-0.3px' },
  h3: { color: COLORS.text, fontSize: '1.1rem', fontWeight: 700, margin: '28px 0 8px' },
  p: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 20 },
  strong: { color: COLORS.text },
  li: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 10 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 28 },
  th: { color: COLORS.accent, fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', padding: '10px 14px', borderBottom: `2px solid ${COLORS.border}`, textAlign: 'left' },
  td: { color: COLORS.muted, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, lineHeight: 1.5 },
  tdFirst: { color: COLORS.text, fontWeight: 600, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}` },
  cta: { display: 'block', margin: '40px 0', padding: '18px 32px', background: COLORS.accent, color: '#000', fontWeight: 800, fontSize: '1rem', borderRadius: 10, textDecoration: 'none', textAlign: 'center', letterSpacing: '-0.2px' },
  callout: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${COLORS.accent}`, borderRadius: 8, padding: '16px 20px', marginBottom: 24 },
};

const faqItems = [
  {
    q: 'What interest rate makes it better to pay off debt than invest?',
    a: 'Most financial planners use 6–7% as the crossover point. If your debt rate is above that, paying it off gives a guaranteed return equivalent to the rate saved. Below 5%, historical stock market returns (~7–10% annually) may beat debt payoff over long periods.',
  },
  {
    q: 'Should I pay off debt before saving for retirement?',
    a: 'Always capture employer 401(k) matching first — it\'s an instant 50–100% return with no risk. After that, high-rate debt (above 8%) generally takes priority over retirement contributions. Low-rate debt (below 5%) can be paid slowly while you also invest.',
  },
  {
    q: 'Is it better to have an emergency fund or pay off debt?',
    a: 'Build a small emergency fund first — $1,000 to $2,000 — before aggressively attacking debt. Without it, any unexpected expense goes straight back on a credit card, undoing your progress. Once you have a buffer, focus on debt.',
  },
  {
    q: 'Can I pay off debt and invest at the same time?',
    a: 'Yes — and it\'s often the right answer. A common approach: capture the employer match, build a small emergency fund, put 70% of extra money toward high-rate debt, and invest 30%. Once the high-rate debt is gone, shift more to investing.',
  },
  {
    q: 'Does paying off a mortgage early beat investing?',
    a: 'For most people with a 3–4% mortgage, investing in a diversified index fund is likely to return more over 15–30 years. However, a paid-off home provides a psychological safety net that has real value. Many people split the difference — invest in tax-advantaged accounts first, then make extra mortgage payments.',
  },
];

export default function PayOffDebtOrInvest() {
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <html lang="en" />
        <title>Pay Off Debt or Invest? The Math-Based Answer | ClearMyDebt</title>
        <meta name="description" content="Should you pay off debt or invest extra money? A clear framework using real numbers to decide — with a free debt payoff calculator." />
        <link rel="canonical" href="https://clearmydebt.app/blog/pay-off-debt-or-invest/" />
        <meta property="og:title" content="Pay Off Debt or Invest? The Math-Based Answer | ClearMyDebt" />
        <meta property="og:description" content="Should you pay off debt or invest extra money? A clear framework using real numbers to decide." />
        <meta property="og:url" content="https://clearmydebt.app/blog/pay-off-debt-or-invest/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/blog/pay-off-debt-or-invest/" />
        <link rel="alternate" hreflang="en" href="https://clearmydebt.app/blog/pay-off-debt-or-invest/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "headline": "Pay Off Debt or Invest? The Math-Based Answer",
              "description": "Should you pay off debt or invest extra money? A clear framework using real numbers to decide.",
              "url": "https://clearmydebt.app/blog/pay-off-debt-or-invest/",
              "publisher": { "@type": "Organization", "name": "ClearMyDebt", "url": "https://clearmydebt.app" },
            },
            {
              "@type": "FAQPage",
              "mainEntity": faqItems.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
          ],
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px 80px' }}>

        <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: 32 }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
          <span style={{ margin: '0 8px' }}>&#8250;</span>
          <span>Blog</span>
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' }}>
            Personal Finance
          </div>
          <h1 style={{ fontSize: 'clamp(26px,5vw,40px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 16px', lineHeight: 1.15 }}>
            Pay Off Debt or Invest?
          </h1>
          <p style={{ color: COLORS.muted, fontSize: '1.1rem', lineHeight: 1.6, margin: 0 }}>
            The honest, math-based answer &#8212; and a simple framework to make the right call for your specific situation.
          </p>
        </div>

        <p style={S.p}>
          &#8220;Should I pay off debt or invest?&#8221; is one of the most common personal finance questions
          &#8212; and it has a real answer that depends on the interest rates involved. This is not a values
          question. It is an arithmetic question with a crossover point that you can calculate.
        </p>
        <p style={S.p}>
          The short version: if your debt interest rate is higher than what you expect to earn investing,
          pay off the debt first. But the full answer has important nuances &#8212; especially around
          employer matching, emergency funds, and tax-advantaged accounts.
        </p>

        <a href="/" style={S.cta}>Calculate Your Debt Payoff Plan &#8212; Free</a>

        <h2 style={S.h2}>The Core Principle: Compare After-Tax Rates</h2>
        <p style={S.p}>
          Every extra dollar you put toward a debt earning 20% APR gives you a guaranteed 20% return &#8212;
          because you are saving exactly that much in future interest charges. That is far better than
          the 4&#8211;5% return of a savings account, and comparable to or better than many investment returns.
        </p>
        <p style={S.p}>
          The S&amp;P 500 has returned roughly 7&#8211;10% annually over long periods (inflation-adjusted:
          ~6&#8211;7%). That is an expected return, not a guaranteed one. A debt at 7% or below is where
          the comparison gets close enough that both approaches are reasonable.
        </p>

        <div style={S.callout}>
          <strong style={S.strong}>The rule of thumb:</strong>{" "}
          <span style={{ color: COLORS.muted }}>Debt above 7% → pay it off first. Debt below 5% → you can reasonably invest instead. Between 5–7% → either approach is defensible; your preference and risk tolerance decide.</span>
        </div>

        <h2 style={S.h2}>The Decision Framework</h2>

        <h3 style={S.h3}>Step 1: Always capture your employer 401(k) match</h3>
        <p style={S.p}>
          If your employer matches 50% or 100% of your 401(k) contributions up to some limit,
          contribute enough to get the full match before paying extra debt. This is a guaranteed
          50&#8211;100% return on money &#8212; nothing in investing or debt payoff beats it.
          The math is unambiguous: always take the match first.
        </p>

        <h3 style={S.h3}>Step 2: Build a small emergency fund</h3>
        <p style={S.p}>
          $1,000 to $2,000 set aside prevents you from re-accumulating debt whenever an unexpected
          expense hits. Without this buffer, a car repair or medical bill goes straight back onto
          a credit card, undoing months of progress. Build the buffer first, then attack debt.
        </p>

        <h3 style={S.h3}>Step 3: Apply the rate comparison</h3>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Debt type</th>
              <th style={S.th}>Typical rate</th>
              <th style={S.th}>Decision</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>Credit cards</td>
              <td style={S.td}>20–29%</td>
              <td style={S.td}>Pay off immediately</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Personal loans</td>
              <td style={S.td}>12–20%</td>
              <td style={S.td}>Pay off before investing</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Car loans</td>
              <td style={S.td}>5–12%</td>
              <td style={S.td}>Lean toward paying off; depends on rate</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Student loans</td>
              <td style={S.td}>4–8%</td>
              <td style={S.td}>Case by case; rate matters</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Mortgage</td>
              <td style={S.td}>3–7%</td>
              <td style={S.td}>Usually invest first (esp. in tax-advantaged accounts)</td>
            </tr>
          </tbody>
        </table>

        <h2 style={S.h2}>The Case for Paying Off High-Rate Debt First</h2>
        <p style={S.p}>
          Beyond the math, eliminating debt has real-world advantages:
        </p>
        <ul style={{ paddingLeft: 24, marginBottom: 24 }}>
          <li style={S.li}><strong style={S.strong}>Guaranteed return.</strong> Investments can drop 30% in a bad year. Paying off a 22% card saves exactly 22% — no market risk.</li>
          <li style={S.li}><strong style={S.strong}>Cash flow improvement.</strong> Every eliminated debt payment frees up monthly income permanently, making you more resilient to job loss.</li>
          <li style={S.li}><strong style={S.strong}>Psychological relief.</strong> Debt causes measurable stress. The mental bandwidth freed when debt is gone often leads to better financial decisions overall.</li>
          <li style={S.li}><strong style={S.strong}>Lower financial risk.</strong> Debt is an obligation that persists regardless of income changes. Investments fluctuate; debt does not disappear when markets fall.</li>
        </ul>

        <h2 style={S.h2}>The Case for Investing While Carrying Low-Rate Debt</h2>
        <p style={S.p}>
          For low-rate debts &#8212; particularly mortgages and subsidised student loans under 5% &#8212;
          the math can favour investing:
        </p>
        <ul style={{ paddingLeft: 24, marginBottom: 24 }}>
          <li style={S.li}><strong style={S.strong}>Time in the market.</strong> Compound growth works over decades. A dollar invested at 30 grows more than a dollar invested at 40, regardless of what you do with debt.</li>
          <li style={S.li}><strong style={S.strong}>Tax advantages.</strong> 401(k) and IRA contributions grow tax-deferred or tax-free. A 4% mortgage effectively costs less than 4% after the mortgage interest deduction (for itemisers).</li>
          <li style={S.li}><strong style={S.strong}>Diversification.</strong> Being debt-free but with no investments means all your net worth is in home equity &#8212; an illiquid, concentrated asset.</li>
        </ul>

        <h2 style={S.h2}>The Balanced Approach: Hybrid Strategy</h2>
        <p style={S.p}>
          For most people carrying mixed debt, a hybrid approach works well:
        </p>
        <ol style={{ paddingLeft: 24, marginBottom: 24 }}>
          <li style={S.li}>Capture full employer 401(k) match</li>
          <li style={S.li}>Build $1,500 emergency fund</li>
          <li style={S.li}>Pay off all debt above 8% aggressively</li>
          <li style={S.li}>Once high-rate debt is gone: split extra money 60% to investing, 40% to low-rate debt</li>
          <li style={S.li}>Max out tax-advantaged accounts (Roth IRA, HSA) before taxable investing</li>
        </ol>

        <div style={S.callout}>
          <strong style={S.strong}>Practical example:</strong>{" "}
          <span style={{ color: COLORS.muted }}>You have $500/month extra. You owe $8,000 at 19% (credit card) and a $120,000 mortgage at 4%. Put the full $500 toward the credit card until it's gone — likely within 18 months. Then redirect $300 to index funds and $200 toward the mortgage. You've optimised both timelines.</span>
        </div>

        <h2 style={S.h2}>Frequently Asked Questions</h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: COLORS.card, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

        <div style={{ ...S.callout, marginTop: 40 }}>
          <p style={{ color: COLORS.text, fontWeight: 700, fontSize: '1.05rem', margin: '0 0 8px' }}>
            Know your debt payoff timeline before you decide
          </p>
          <p style={{ color: COLORS.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
            Enter your debts into the free calculator and see exactly when you&#8217;d be debt-free &#8212;
            and how much interest you&#8217;ll save. That number is the guaranteed return of paying off debt.
          </p>
          <a href="/" style={{ ...S.cta, margin: 0 }}>Calculate Your Debt Payoff</a>
        </div>

      </div>
    </div>
  );
}
